import { useState, useMemo, useEffect } from "react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import { Bell, AlertTriangle, XCircle, CheckCircle2, PackageX, X, KeyRound, Wrench, Fuel, CreditCard } from "lucide-react";

const keyOf = (i) => `${i.employee}|${i.document}|${i.expiry_date}`;
const remaining = (d) => (d < 0 ? `منتهية منذ ${Math.abs(d)} يوم` : d === 0 ? "اليوم" : `خلال ${d} يوم`);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("ar-EG"); } catch { return ""; } };

export const NotificationBell = ({ items = [], alerts = [], storageKey, onSelect, onAlertsOpen, onAlertClick, onAlertDismiss }) => {
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(new Set());

  useEffect(() => {
    try { setSeen(new Set(JSON.parse(localStorage.getItem(storageKey) || "[]"))); }
    catch { setSeen(new Set()); }
  }, [storageKey]);

  const keys = useMemo(() => items.map(keyOf), [items]);
  const unseenCount = useMemo(() => keys.filter((k) => !seen.has(k)).length, [keys, seen]);
  const alertsUnread = useMemo(() => alerts.filter((a) => !a.read).length, [alerts]);
  const badge = unseenCount + alertsUnread;

  useEffect(() => {
    if (open) {
      if (unseenCount > 0) {
        const next = new Set(keys);
        setSeen(next);
        try { localStorage.setItem(storageKey, JSON.stringify([...next])); } catch {}
      }
      if (alertsUnread > 0) onAlertsOpen?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const total = items.length + alerts.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" aria-label="الإشعارات"
          className="relative rounded-full hover:bg-slate-100 p-2 transition">
          <Bell className="h-5 w-5 text-slate-600" />
          {badge > 0 && (
            <span data-testid="notification-badge"
              className="absolute -top-0.5 -left-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center font-mono-nums">
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0 font-body" dir="rtl" data-testid="notification-panel">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <span className="font-heading font-bold text-slate-900">الإشعارات</span>
          <span className="text-xs text-slate-400">{total} تنبيه</span>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {alerts.length > 0 && (
            <div data-testid="custody-alerts-section">
              <div className="px-4 pt-3 pb-1 text-[11px] font-bold text-red-600 flex items-center gap-1.5">
                <PackageX className="h-3.5 w-3.5" /> تنبيهات تحتاج إجراءً
              </div>
              {alerts.map((a) => {
                const isReset = a.type === "password_reset";
                const isLow = a.type === "low_stock";
                const isFuel = a.type === "fuel_suspicious";
                const isCard = a.type === "opcard_expiry";
                const isRecv = a.type === "card_received";
                const isPriv = a.type === "private_doc_expiry" || a.type === "rental_overdue";
                const isInel = a.type === "ineligible_riders";
                const Icon = isCard ? CreditCard : isFuel ? Fuel : isReset ? KeyRound : isLow ? Wrench : PackageX;
                return (
                <div key={a.id} data-testid={`custody-alert-${a.id}`}
                  className={`w-full flex items-start gap-3 px-4 py-3 border-b border-slate-50 transition ${a.read ? "" : (isReset ? "bg-amber-50/40" : isLow ? "bg-blue-50/40" : "bg-red-50/40")}`}>
                  <button onClick={() => { onAlertClick?.(a); setOpen(false); }}
                    className="flex items-start gap-3 flex-1 text-right hover:opacity-80 transition min-w-0">
                    <div className={`mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${isReset ? "bg-amber-50" : isLow ? "bg-blue-50" : "bg-red-50"}`}>
                      <Icon className={`h-4 w-4 ${isReset ? "text-amber-600" : isLow ? "text-blue-600" : "text-red-600"}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{isLow ? a.part_name : a.employee_name}</p>
                      {isCard ? (
                        <div className="text-xs text-red-700 space-y-0.5" data-testid={`opcard-alert-text-${a.id}`}>
                          {(a.cards || []).slice(0, 4).map((c) => (
                            <p key={c.number}>كرت <b dir="ltr">{c.number}</b>{c.plate ? ` (${c.plate})` : ""} — {c.days_left < 0 ? "منتهٍ" : `بعد ${c.days_left} يوم`}{c.riders?.length ? ` · ${c.riders.join("، ")}` : ""}</p>
                          ))}
                          {(a.cards || []).length > 4 && <p>و{a.cards.length - 4} كروت أخرى…</p>}
                        </div>
                      ) : isFuel ? (
                        <p className="text-xs text-red-700" data-testid={`fuel-alert-text-${a.id}`}>
                          ملف واعي {a.month}: {a.count} تعبئة تتجاوز {a.limit} لتر بمبلغ {Number(a.amount || 0).toLocaleString("en-US")} ⃁
                        </p>
                      ) : isPriv ? (
                        <p className="text-xs text-amber-700" data-testid={`private-doc-alert-${a.id}`}>{a.message}</p>
                      ) : isInel ? (
                        <p className="max-h-40 overflow-y-auto whitespace-pre-line text-xs leading-5 text-rose-700" data-testid={`ineligible-alert-${a.id}`}>{a.message}</p>
                      ) : isRecv ? (
                        <p className="text-xs text-emerald-700" data-testid={`card-received-alert-${a.id}`}>أكّد استلام بطاقة المندوب المطبوعة</p>
                      ) : isReset ? (
                        <p className="text-xs text-amber-700">طلب إعادة تعيين كلمة المرور · <span dir="ltr">{a.phone}</span></p>
                      ) : isLow ? (
                        <p className="text-xs text-blue-700">
                          نقص مخزون — المتبقّي {a.quantity} (الحد {a.min_alert}){a.warehouse_name ? ` · ${a.warehouse_name}` : ""}
                        </p>
                      ) : a.message && !a.items ? (
                        <p className="max-h-40 overflow-y-auto whitespace-pre-line text-xs leading-5 text-slate-600">{a.message}</p>
                      ) : (
                        <>
                          <p className="text-xs text-slate-500">
                            غيّر حالته إلى «{a.employee_status || "غير نشط"}» وبحوزته {a.items?.length || 0} عهدة
                          </p>
                          <p className="text-xs text-red-600 truncate">
                            {(a.items || []).map((it) => it.type + (it.identifier ? ` (${it.identifier})` : "")).join("، ")}
                          </p>
                        </>
                      )}
                      <p className="text-[11px] text-slate-400">{fmtDate(a.created_at)}</p>
                    </div>
                  </button>
                  <button data-testid={`dismiss-alert-${a.id}`} onClick={() => onAlertDismiss?.(a)}
                    className="text-slate-300 hover:text-red-600 transition p-0.5 shrink-0" title="إخفاء">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                );
              })}
            </div>
          )}

          {items.length > 0 && (
            <div className="px-4 pt-3 pb-1 text-[11px] font-bold text-amber-600 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> وثائق تحتاج انتباهك
            </div>
          )}
          {total === 0 ? (
            <div className="py-10 text-center text-slate-400">
              <CheckCircle2 className="h-8 w-8 mx-auto text-blue-400" />
              <p className="mt-2 text-sm">لا توجد تنبيهات. كل شيء على ما يرام!</p>
            </div>
          ) : (
            items.map((i, idx) => {
              const expired = i.status === "expired";
              return (
                <button key={idx} data-testid={`notification-item-${idx}`}
                  onClick={() => { onSelect?.(i.employee); setOpen(false); }}
                  className="w-full text-right flex items-start gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition">
                  <div className={`mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${expired ? "bg-red-50" : "bg-amber-50"}`}>
                    {expired ? <XCircle className="h-4 w-4 text-red-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{i.employee}</p>
                    <p className="text-xs text-slate-500">{i.document} · <span className={expired ? "text-red-600" : "text-amber-600"}>{remaining(i.days_left)}</span></p>
                    <p className="text-[11px] text-slate-400 font-mono-nums">{i.expiry_date}</p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
